/**
 * Trunnion AI - Search Result Card Component
 *
 * Polymorphic card that renders differently based on entity type.
 * Each variant shows type-specific icons, metadata, and links to
 * the entity's detail page.
 */

import { Link } from 'react-router-dom';
import { GitBranch, Bot, Play, FileText, Clock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { ROUTES } from '@/config/routes';
import type {
  SearchResult,
  SearchEntityType,
  WorkflowSearchMetadata,
  AgentSearchMetadata,
  ExecutionSearchMetadata,
  AuditSearchMetadata,
  ClassificationLevel,
} from '@/types';

// ── Status badge variant mapping ────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'purple';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  // Workflow statuses
  draft: 'default',
  published: 'success',
  archived: 'warning',
  // Execution statuses
  queued: 'default',
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
  waiting_approval: 'purple',
  paused_for_approval: 'purple',
  timed_out: 'error',
};

function getStatusVariant(status: string): BadgeVariant {
  return STATUS_VARIANTS[status.toLowerCase()] ?? 'default';
}

// ── Tier badge variant mapping ──────────────────────────────────────────

const TIER_VARIANTS: Record<string, BadgeVariant> = {
  brain: 'purple',
  execution_proxy: 'info',
  console_automation: 'default',
  orchestrator: 'purple',
  specialist: 'info',
  utility: 'default',
};

function getTierVariant(tier: string): BadgeVariant {
  return TIER_VARIANTS[tier.toLowerCase()] ?? 'default';
}

// ── Relative time formatting ────────────────────────────────────────────

function formatRelativeTime(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

// ── Duration formatting ─────────────────────────────────────────────────

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '--';
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

// ── Entity-specific route resolution ────────────────────────────────────

function getDetailRoute(result: SearchResult): string {
  switch (result.type) {
    case 'workflow':
      return ROUTES.WORKFLOW_DETAIL(result.id);
    case 'agent':
      return ROUTES.AGENT_DETAIL(result.id);
    case 'execution':
      return ROUTES.EXECUTION_DETAIL(result.id);
    case 'audit':
      return ROUTES.AUDIT;
    default:
      return ROUTES.DASHBOARD;
  }
}

// ── Entity type label ───────────────────────────────────────────────────

const TYPE_LABELS: Record<SearchEntityType, string> = {
  workflow: 'Workflow',
  agent: 'Agent',
  execution: 'Execution',
  audit: 'Audit Event',
};

// ── Type-specific icon ──────────────────────────────────────────────────

function TypeIcon({ type }: { type: SearchEntityType }) {
  const iconClass = 'h-5 w-5 shrink-0';

  switch (type) {
    case 'workflow':
      return <GitBranch className={`${iconClass} text-trunnion-gold-400`} />;
    case 'agent':
      return <Bot className={`${iconClass} text-purple-400`} />;
    case 'execution':
      return <Play className={`${iconClass} text-blue-400`} />;
    case 'audit':
      return <FileText className={`${iconClass} text-trunnion-slate-400`} />;
    default:
      return <FileText className={`${iconClass} text-trunnion-slate-400`} />;
  }
}

// ── Type-specific metadata rendering ────────────────────────────────────

function WorkflowMeta({ metadata }: { metadata: WorkflowSearchMetadata }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant={getStatusVariant(metadata.status)} dot>
        {metadata.status}
      </Badge>
      <span className="text-xs text-trunnion-slate-400">
        v{metadata.version}
      </span>
      <span className="text-xs text-trunnion-slate-500">
        {metadata.nodeCount} {metadata.nodeCount === 1 ? 'node' : 'nodes'}
      </span>
      {metadata.tags.length > 0 && (
        <span className="text-xs text-trunnion-slate-500">
          {metadata.tags.slice(0, 3).join(', ')}
          {metadata.tags.length > 3 && ` +${metadata.tags.length - 3}`}
        </span>
      )}
    </div>
  );
}

function AgentMeta({ metadata }: { metadata: AgentSearchMetadata }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant={getTierVariant(metadata.tier)}>
        {metadata.tier.replace('_', ' ')}
      </Badge>
      {metadata.model && (
        <span className="text-xs text-trunnion-slate-400 font-mono">
          {metadata.model}
        </span>
      )}
      {!metadata.isActive && (
        <Badge variant="error" dot>
          Revoked
        </Badge>
      )}
    </div>
  );
}

function ExecutionMeta({ metadata }: { metadata: ExecutionSearchMetadata }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant={getStatusVariant(metadata.status)} dot>
        {metadata.status}
      </Badge>
      {metadata.durationMs != null && (
        <span className="inline-flex items-center gap-1 text-xs text-trunnion-slate-400">
          <Clock className="h-3 w-3" />
          {formatDuration(metadata.durationMs)}
        </span>
      )}
      <span className="text-xs text-trunnion-slate-500">
        {metadata.triggerType}
      </span>
    </div>
  );
}

function AuditMeta({ metadata }: { metadata: AuditSearchMetadata }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant="default">
        {metadata.eventType}
      </Badge>
      <span className="text-xs text-trunnion-slate-400">
        by {metadata.actorName}
      </span>
      {metadata.resourceType && (
        <span className="text-xs text-trunnion-slate-500">
          {metadata.resourceType}
        </span>
      )}
    </div>
  );
}

function ResultMetadata({ result }: { result: SearchResult }) {
  switch (result.type) {
    case 'workflow':
      return <WorkflowMeta metadata={result.metadata as WorkflowSearchMetadata} />;
    case 'agent':
      return <AgentMeta metadata={result.metadata as AgentSearchMetadata} />;
    case 'execution':
      return <ExecutionMeta metadata={result.metadata as ExecutionSearchMetadata} />;
    case 'audit':
      return <AuditMeta metadata={result.metadata as AuditSearchMetadata} />;
    default:
      return null;
  }
}

// ── Main component ──────────────────────────────────────────────────────

interface SearchResultCardProps {
  result: SearchResult;
}

export function SearchResultCard({ result }: SearchResultCardProps) {
  const route = getDetailRoute(result);

  return (
    <Link to={route} className="block">
      <Card hover padding="sm" className="group">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="mt-0.5">
            <TypeIcon type={result.type} />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1 space-y-1.5">
            {/* Title row */}
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-medium text-white truncate group-hover:text-trunnion-gold-400 transition-colors">
                {result.title}
              </h4>
              <ClassificationTag
                level={result.classification as ClassificationLevel}
                size="sm"
                showIcon={false}
              />
              <span className="ml-auto text-xs text-trunnion-slate-500 uppercase tracking-wide shrink-0">
                {TYPE_LABELS[result.type]}
              </span>
            </div>

            {/* Description */}
            {result.description && (
              <p className="text-xs text-trunnion-slate-400 line-clamp-1">
                {result.description}
              </p>
            )}

            {/* Type-specific metadata row */}
            <div className="flex items-center justify-between gap-2">
              <ResultMetadata result={result} />
              <span className="text-xs text-trunnion-slate-500 shrink-0">
                {formatRelativeTime(result.updatedAt)}
              </span>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
